import { addToolBreadcrumb } from '@tunasong/models'
import {
  computeIntervalSessionStats,
  computeQualitySessionStats,
  generateIntervalQuestion,
  generateQualityQuestion,
  validateIntervalAnswer,
  validateQualityAnswer,
} from '@tunasong/music-lib'
import type {
  ChordQualityKind,
  IntervalKind,
  IntervalQuestion,
  IntervalSessionResult,
  IntervalVerdict,
  QualityQuestion,
  QualitySessionResult,
  QualityVerdict,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { EarTrainingSummary } from './ear-training-summary.js'
import { EarTrainingWelcome } from './ear-training-welcome.js'
import { IntervalFeedback } from './interval-feedback.js'
import { IntervalQuestionPanel } from './interval-question.js'
import { useIntervalPlayback } from './interval-playback.js'
import { parseSubMode, type SubMode } from './ear-training-params.js'
import { QualityFeedback } from './quality-feedback.js'
import { QualityQuestionPanel } from './quality-question.js'
import { useQualityPlayback } from './quality-playback.js'
import styles from './ear-training.module.css'

const QUESTIONS_PER_SESSION = 10
const AUTOPLAY_DELAY_MS = 250

type SessionState =
  | { phase: 'welcome'; preSelected: SubMode | null }
  | { phase: 'asking-interval'; index: number; question: IntervalQuestion; results: IntervalSessionResult[] }
  | {
      phase: 'feedback-interval'
      index: number
      question: IntervalQuestion
      verdict: IntervalVerdict
      results: IntervalSessionResult[]
    }
  | { phase: 'done-interval'; results: IntervalSessionResult[] }
  | { phase: 'asking-quality'; index: number; question: QualityQuestion; results: QualitySessionResult[] }
  | {
      phase: 'feedback-quality'
      index: number
      question: QualityQuestion
      verdict: QualityVerdict
      results: QualitySessionResult[]
    }
  | { phase: 'done-quality'; results: QualitySessionResult[] }

/**
 * Ear Training Suite v1 host. State machine:
 *   welcome → asking-{interval|quality} → feedback-{interval|quality}
 *           → asking … (×10) → done-{interval|quality}
 *
 * Owns the session state, generates new questions per round, validates the
 * user's pick via the analyzer, and stitches the panels together. Per Bowie
 * §Q5 the sub-mode is locked for the session — switching requires returning
 * to the welcome screen (the host pre-selects the OTHER card on switch).
 *
 * Audition autoplay: when a new question mounts (phase becomes asking-*) the
 * audition plays after a short delay so the panel paints first. Manual
 * "Hear it" / "Hear it again" triggers fresh playback.
 *
 * The Related-tools footer is shown on welcome only — mid-session it would
 * pull attention from the listening task.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4
 */
export const EarTraining: FC = () => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<SessionState>(() => ({
    phase: 'welcome',
    preSelected: parseSubMode(searchParams.get('mode')),
  }))

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'ear-training' })
  }, [])

  // Active questions for playback hooks. The hooks are mounted unconditionally
  // — a null question yields an empty stab list (no-op play).
  const intervalQuestion: IntervalQuestion | null =
    state.phase === 'asking-interval' || state.phase === 'feedback-interval' ? state.question : null
  const qualityQuestion: QualityQuestion | null =
    state.phase === 'asking-quality' || state.phase === 'feedback-quality' ? state.question : null
  const { play: playInterval } = useIntervalPlayback({ question: intervalQuestion })
  const { play: playQuality } = useQualityPlayback({ question: qualityQuestion })

  // Autoplay on each new asking-* mount. The effect dep is the question
  // identity, so asking → feedback → asking (next question) re-triggers.
  const askingIntervalQuestion = state.phase === 'asking-interval' ? state.question : null
  useEffect(() => {
    if (askingIntervalQuestion === null) {
      return
    }
    const handle = window.setTimeout(playInterval, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingIntervalQuestion, playInterval])
  const askingQualityQuestion = state.phase === 'asking-quality' ? state.question : null
  useEffect(() => {
    if (askingQualityQuestion === null) {
      return
    }
    const handle = window.setTimeout(playQuality, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingQualityQuestion, playQuality])

  const startInterval = () => {
    setState({ phase: 'asking-interval', index: 0, question: generateIntervalQuestion(), results: [] })
  }
  const startQuality = () => {
    setState({ phase: 'asking-quality', index: 0, question: generateQualityQuestion(), results: [] })
  }
  const start = (subMode: SubMode) => {
    if (subMode === 'interval') {
      startInterval()
    } else {
      startQuality()
    }
  }

  const answerInterval = (picked: IntervalKind) => {
    if (state.phase !== 'asking-interval') {
      return
    }
    const verdict = validateIntervalAnswer(state.question, picked)
    addToolBreadcrumb('answered', { tool: 'ear-training', subMode: 'interval', correct: verdict.correct })
    setState({
      phase: 'feedback-interval',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
    })
  }
  const answerQuality = (picked: ChordQualityKind) => {
    if (state.phase !== 'asking-quality') {
      return
    }
    const verdict = validateQualityAnswer(state.question, picked)
    addToolBreadcrumb('answered', { tool: 'ear-training', subMode: 'quality', correct: verdict.correct })
    setState({
      phase: 'feedback-quality',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
    })
  }

  const nextInterval = () => {
    if (state.phase !== 'feedback-interval') {
      return
    }
    const results: IntervalSessionResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-interval', results })
      return
    }
    setState({
      phase: 'asking-interval',
      index: state.index + 1,
      question: generateIntervalQuestion(),
      results,
    })
  }
  const nextQuality = () => {
    if (state.phase !== 'feedback-quality') {
      return
    }
    const results: QualitySessionResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quality', results })
      return
    }
    setState({
      phase: 'asking-quality',
      index: state.index + 1,
      question: generateQualityQuestion(),
      results,
    })
  }

  /**
   * Switch from a done state back to welcome with the OPPOSITE sub-mode
   * pre-selected — mirrors Joni's expected "I want the other quiz" intent.
   */
  const switchMode = (lastSubMode: SubMode) => {
    const otherMode: SubMode = lastSubMode === 'interval' ? 'quality' : 'interval'
    setState({ phase: 'welcome', preSelected: otherMode })
  }

  const footerTools: RelatedTool[] = [
    { targetRoute: '/quiz', lexiconId: 'crosslink-ear-to-quiz' },
    { targetRoute: '/modes', lexiconId: 'crosslink-ear-to-mode' },
    { targetRoute: '/reharm', lexiconId: 'crosslink-ear-to-reharm' },
    { targetRoute: '/cadences', lexiconId: 'crosslink-ear-to-cadence' },
  ]

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <EarTrainingWelcome preSelected={state.preSelected} onStart={start} />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'asking-interval') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <IntervalQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playInterval}
          onAnswer={answerInterval}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-interval') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <IntervalQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playInterval}
          onAnswer={() => {
            /* feedback phase — disabled via interactive={false} */
          }}
        />
        <IntervalFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={playInterval}
          onNext={nextInterval}
        />
      </div>
    )
  }

  if (state.phase === 'asking-quality') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <QualityQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playQuality}
          onAnswer={answerQuality}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-quality') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <QualityQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playQuality}
          onAnswer={() => {
            /* feedback phase — disabled via interactive={false} */
          }}
        />
        <QualityFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={playQuality}
          onNext={nextQuality}
        />
      </div>
    )
  }

  if (state.phase === 'done-interval') {
    return (
      <div className={styles.container}>
        <EarTrainingSummary
          subMode="interval"
          stats={computeIntervalSessionStats(state.results)}
          onRestart={startInterval}
          onSwitchMode={switchMode}
        />
      </div>
    )
  }

  // state.phase === 'done-quality'
  return (
    <div className={styles.container}>
      <EarTrainingSummary
        subMode="quality"
        stats={computeQualitySessionStats(state.results)}
        onRestart={startQuality}
        onSwitchMode={switchMode}
      />
    </div>
  )
}
